import { PSCMenuTransformer } from './services/PSC-api-transformer';
import { SRAMenuTransformer } from './services/SRA-api-transformer';
import {
  ADMIN_SCOPE,
  ADMIN_SLUG,
  AIDR_SCOPE,
  AI_COWORKERS_SLUG,
  APIInformation,
  APIMenuDefinition,
  ENTITLE_SCOPE,
  ENTITLE_SLUG,
  EPML_SCOPE,
  EPML_SLUG,
  INSIGHTS_SCOPE,
  INSIGHTS_SLUG,
  PASSWORD_SAFE_SCOPE,
  PASSWORD_SAFE_SLUG,
  PLATFORM_SCOPE,
  PLATFORM_SLUG,
  PMC_SCOPE,
  PMC_SLUG,
  PRIVILEGED_REMOTE_ACCESS_SCOPE,
  PRIVILEGED_REMOTE_ACCESS_SLUG,
  ProductMenuItem,
  ProductSlug,
  REMOTE_SUPPORT_SCOPE,
  REMOTE_SUPPORT_SLUG,
  SECRETS_SCOPE,
  SECRETS_SLUG,
} from './types';

export const AUTHORIZATION_URL = `/session/login?rd=` as const;
export const LOGOUT_URL = '/session/logout' as const;
export const ADMIN_BASE_PATH = '/admin' as const;
export const INSIGHTS_BASE_PATH = '/insights' as const;
export const EPML_BASE_PATH = '/epm/linux' as const;
export const EPMWM_BASE_PATH = '/epm/win-mac' as const;
export const ENTITLE_BASE_PATH = '/entitle' as const;
export const SECRETS_BASE_PATH = '/secrets' as const;
export const WLC_BASE_PATH = '/wlc' as const;
export const PSC_BASE_PATH = '/password-safe' as const;
export const PRA_BASE_PATH = '/pra' as const;
export const RS_BASE_PATH = '/rs' as const;
export const AI_COWORKERS_BASE_PATH = '/ai-security' as const;
// INFO(kohearon): The menu item should NEVER be included
// via the future API call, and instead shall be defined
// here.
export const PLATFORM_HOME_PRODUCT_MENU_ITEM: ProductMenuItem = {
  name: 'BeyondTrust Pathfinder Home',
  slug: PLATFORM_SLUG,
  baseHref: '/',
  productInformationUrl: '',
  helpUrl: 'https://docs.beyondtrust.com/',
  scopeName: PLATFORM_SCOPE,
  menuSlotName: 'platform-menu',
};

// AI Agent Security is a full product: its menu item is shown whenever the
// site is entitled to the `AIDR` product (see PRODUCT_MENU_ITEMS below and
// `resolveProductMenuItems`), matched by its `aidr` scopeName.
export const AI_COWORKERS_PRODUCT_MENU_ITEM: ProductMenuItem = {
  name: 'AI Agent Security',
  slug: AI_COWORKERS_SLUG,
  baseHref: AI_COWORKERS_BASE_PATH,
  // Bookmarks to the pre-rename path still highlight the product.
  altBaseHrefs: ['/ai-coworkers'],
  productInformationUrl: 'https://www.beyondtrust.com/products/ai-agent-security',
  helpUrl: 'https://docs.beyondtrust.com/bt-docs/docs/ai-agent-security',
  scopeName: AIDR_SCOPE,
  menuSlotName: 'ai-coworkers-menu',
};

/** Gates the language picker the profile card renders. */
export const LANGUAGE_SELECTOR_FLAG = 'language-selector' as const;
export const WLC_PRODUCT_NAME = 'Workload Credentials' as const;

/**
 * Products with an entry in the cross-product Reporting menu. Must match the
 * products platform-reporting-menu renders (reporting-menu.ts).
 */
export const REPORTING_PRODUCTS: ProductSlug[] = [
  INSIGHTS_SLUG,
  EPML_SLUG,
  PMC_SLUG,
  PASSWORD_SAFE_SLUG,
  REMOTE_SUPPORT_SLUG,
  PRIVILEGED_REMOTE_ACCESS_SLUG,
  ENTITLE_SLUG,
];

// INFO(kohearon): This will eventually come from
// and API point determining the product(s) for which
// a user has access.
export const PRODUCT_MENU_ITEMS: ProductMenuItem[] = [
  {
    name: 'Administration',
    slug: ADMIN_SLUG,
    baseHref: ADMIN_BASE_PATH,
    productInformationUrl: '',
    helpUrl: 'https://docs.beyondtrust.com/',
    isAdminApp: true,
    scopeName: ADMIN_SCOPE,
    menuSlotName: 'admin-menu',
  },
  {
    name: 'Insights',
    slug: INSIGHTS_SLUG,
    baseHref: INSIGHTS_BASE_PATH,
    productInformationUrl: 'https://www.beyondtrust.com/products/identity-security-insights',
    helpUrl: 'https://www.beyondtrust.com/docs/identity-security-insights/index.htm',
    scopeName: INSIGHTS_SCOPE,
    menuSlotName: 'insights-menu',
  },
  {
    name: 'Endpoint Privilege Management for Linux',
    slug: EPML_SLUG,
    baseHref: EPML_BASE_PATH,

    productInformationUrl: 'https://www.beyondtrust.com/products/endpoint-privilege-management/features/linux',
    helpUrl: 'https://www.beyondtrust.com/docs/privilege-management/unix-linux/index.htm',
    scopeName: EPML_SCOPE,
    menuSlotName: 'epml-menu',
  },
  {
    name: 'Endpoint Privilege Management for Windows and Mac',
    slug: PMC_SLUG,
    baseHref: EPMWM_BASE_PATH,
    productInformationUrl: 'https://www.beyondtrust.com/products/endpoint-privilege-management/features/windows-mac',
    helpUrl: 'https://www.beyondtrust.com/docs/privilege-management/windows-and-mac.htm',
    scopeName: PMC_SCOPE,
    menuSlotName: 'pmc-menu',
  },
  {
    name: 'Password Safe',
    slug: PASSWORD_SAFE_SLUG,
    baseHref: PSC_BASE_PATH,
    productInformationUrl: 'https://www.beyondtrust.com/products/password-safe',
    helpUrl: 'https://www.beyondtrust.com/docs/beyondinsight-password-safe/ps/index.htm',
    scopeName: PASSWORD_SAFE_SCOPE,
    menuSlotName: 'passwordsafe-menu',
  },
  {
    name: 'Remote Support',
    slug: REMOTE_SUPPORT_SLUG,
    baseHref: RS_BASE_PATH,
    productInformationUrl: 'https://www.beyondtrust.com/products/remote-support',
    helpUrl: 'https://www.beyondtrust.com/docs/remote-support/index.htm',
    scopeName: REMOTE_SUPPORT_SCOPE,
    menuSlotName: 'remote-support-menu',
  },
  {
    name: 'Privileged Remote Access',
    slug: PRIVILEGED_REMOTE_ACCESS_SLUG,
    baseHref: PRA_BASE_PATH,
    productInformationUrl: 'https://www.beyondtrust.com/products/privileged-remote-access',
    helpUrl: 'https://www.beyondtrust.com/docs/privileged-remote-access/index.htm',
    scopeName: PRIVILEGED_REMOTE_ACCESS_SCOPE,
    menuSlotName: 'privileged-remote-access-menu',
  },
  {
    name: WLC_PRODUCT_NAME,
    slug: SECRETS_SLUG,
    baseHref: WLC_BASE_PATH,
    // Workload Credentials moved from /secrets; a bookmark to the old path still resolves to it.
    altBaseHrefs: [SECRETS_BASE_PATH],
    productInformationUrl: 'https://docs.beyondtrust.com/',
    helpUrl: 'https://docs.beyondtrust.com/',
    scopeName: SECRETS_SCOPE,
    menuSlotName: 'secrets-menu',
  },
  {
    name: 'Entitle',
    slug: ENTITLE_SLUG,
    baseHref: ENTITLE_BASE_PATH,
    productInformationUrl: 'https://www.entitle.io/',
    helpUrl: 'https://docs.beyondtrust.com/entitle/docs/welcome',
    scopeName: ENTITLE_SCOPE,
    menuSlotName: 'entitle-menu',
  },
  AI_COWORKERS_PRODUCT_MENU_ITEM,
];

export const ProductAPIMap: Partial<Record<ProductSlug, APIInformation>> = {
  [ADMIN_SLUG]: {
    apiURL: '/admin/api/platform/sitemenu',
    baseURL: ADMIN_BASE_PATH,
  },
  [INSIGHTS_SLUG]: {
    apiURL: '/insights/api/insights/sitemenu',
    baseURL: '/insights/',
  },
  [EPML_SLUG]: {
    apiURL: '/epm/linux/api/v2/sitemenu',
    baseURL: '/epm/linux/',
  },
  [PASSWORD_SAFE_SLUG]: {
    apiURL: '/password-safe/webconsole/platform/sitemenu',
    baseURL: '/',
    transformer: PSCMenuTransformer,
  },
  [ENTITLE_SLUG]: {
    apiURL: '/entitle/client/routes',
    baseURL: '/entitle/',
  },
  [REMOTE_SUPPORT_SLUG]: {
    apiURL: '/rs/login/nav',
    baseURL: '/rs/',
    transformer: SRAMenuTransformer,
  },
  [PRIVILEGED_REMOTE_ACCESS_SLUG]: {
    apiURL: '/pra/login/nav',
    baseURL: '/pra/',
    transformer: SRAMenuTransformer,
  },
  [PMC_SLUG]: {
    apiURL: '/epm/win-mac/api/portal-api/v1/config/menu',
    baseURL: '/',
  },
  [AI_COWORKERS_SLUG]: {
    apiURL: '/ai-security/api/menu',
    baseURL: '/ai-security/',
  },
};

/**
 * Products whose second-level menu ships with the library as a static
 * definition instead of coming from a menu API.
 */
export const STATIC_PRODUCT_MENUS: Partial<Record<ProductSlug, APIMenuDefinition>> = {
  [SECRETS_SLUG]: {
    features: [{ id: 'secrets', name: WLC_PRODUCT_NAME, link: WLC_BASE_PATH, items: [] }],
  },
};

/** Whether a product has second-level menu content (API-driven or static). */
export function hasProductMenu(slug: ProductSlug): boolean {
  return ProductAPIMap[slug] != undefined || STATIC_PRODUCT_MENUS[slug] != undefined;
}
